import React, { useState, useEffect } from 'react';
import CountUp from "react-countup";
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Col, Tooltip } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

interface DateFilter {
    type?: 'day' | 'month' | 'year' | 'period';
    value?: string | number | Date;
    start?: Date;
    end?: Date;
}

interface WidgetsProps {
    dateFilter?: DateFilter;
}

const WidgetsE = ({ dateFilter }: WidgetsProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});
    
    // Structure de données pour allcountprep - Mise à jour avec preparation_orders
    const [data, setData] = useState({
        shipments: {
            total_count: 0,
            by_status: {
                draft: 0,
                validated: 0,
                closed: 0,
                cancelled: 0
            }
        },
        supplier_orders: {
            total_count: 0,
            by_status: {
                draft: 0,
                validated: 0,
                closed: 0,
                cancelled: 0
            }
        },
        preparation_orders: {  // Changé de 'receptions' à 'preparation_orders'
            total_count: 0,
            comptoir: {
                total_count: 0,
                by_status: {
                    draft: 0,
                    validated: 0,
                    closed: 0,
                    cancelled: 0
                }
            },
            franchises: {
                total_count: 0,
                by_status: {
                    draft: 0,
                    validated: 0,
                    closed: 0,
                    cancelled: 0
                }
            }
        },
        pending_orders: {
            comptoir: 0,
            franchises: 0,
            total: 0
        },
        summary: {
            total_documents: 0
        }
    });

    // Fonction pour formater la date
    const formatDate = (date: string | number | Date | undefined): string => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Fonction pour construire les paramètres de filtre de date
    const buildDateFilterParams = () => {
        if (!dateFilter) return {};

        switch (dateFilter.type) {
            case 'day':
                return {
                    filter_type: 'day',
                    filter_value: formatDate(dateFilter.value)
                };
            case 'month':
                return {
                    filter_type: 'month',
                    filter_value: dateFilter.value
                };
            case 'year':
                return {
                    filter_type: 'year',
                    filter_value: dateFilter.value
                };
            case 'period':
                return {
                    filter_type: 'period',
                    date_start: formatDate(dateFilter.start),
                    date_end: formatDate(dateFilter.end)
                };
            default:
                return {};
        }
    };

    const toggleTooltip = (id: string) => {
        setTooltipOpen(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const fetchAllData = async () => {
        if (!user?.entity) return;
        
        setLoading(true);
        
        try {
            const params: any = {
                action: 'allcountprep',
                entity: user.entity
            };

            // Ajouter les filtres de date si fournis
            const dateFilters = buildDateFilterParams();
            Object.assign(params, dateFilters);

            console.log('📤 Paramètres allcountprep:', params);

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data && response.success) {
                const apiData = response.data;
                
                setData({
                    shipments: {
                        total_count: apiData.shipments?.total_count || 0,
                        by_status: {
                            draft: apiData.shipments?.by_status?.draft || 0,
                            validated: apiData.shipments?.by_status?.validated || 0,
                            closed: apiData.shipments?.by_status?.closed || 0,
                            cancelled: apiData.shipments?.by_status?.cancelled || 0
                        }
                    },
                    supplier_orders: {
                        total_count: apiData.supplier_orders?.total_count || 0,
                        by_status: {
                            draft: apiData.supplier_orders?.by_status?.draft || 0,
                            validated: apiData.supplier_orders?.by_status?.validated || 0,
                            closed: apiData.supplier_orders?.by_status?.closed || 0,
                            cancelled: apiData.supplier_orders?.by_status?.cancelled || 0
                        }
                    },
                    preparation_orders: {  // Changé pour correspondre à la nouvelle structure
                        total_count: apiData.preparation_orders?.total_count || 0,
                        comptoir: {
                            total_count: apiData.preparation_orders?.comptoir?.total_count || 0,
                            by_status: {
                                draft: apiData.preparation_orders?.comptoir?.by_status?.draft || 0,
                                validated: apiData.preparation_orders?.comptoir?.by_status?.validated || 0,
                                closed: apiData.preparation_orders?.comptoir?.by_status?.closed || 0,
                                cancelled: apiData.preparation_orders?.comptoir?.by_status?.cancelled || 0
                            }
                        },
                        franchises: {
                            total_count: apiData.preparation_orders?.franchises?.total_count || 0,
                            by_status: {
                                draft: apiData.preparation_orders?.franchises?.by_status?.draft || 0,
                                validated: apiData.preparation_orders?.franchises?.by_status?.validated || 0,
                                closed: apiData.preparation_orders?.franchises?.by_status?.closed || 0,
                                cancelled: apiData.preparation_orders?.franchises?.by_status?.cancelled || 0
                            }
                        }
                    },
                    pending_orders: {
                        comptoir: apiData.pending_orders?.comptoir || 0,
                        franchises: apiData.pending_orders?.franchises || 0,
                        total: apiData.pending_orders?.total || 0
                    },
                    summary: {
                        total_documents: apiData.summary?.total_documents || 0
                    }
                });

                console.log('✅ Données allcountprep reçues:', apiData);
            } else {
                console.error('❌ Erreur API:', response.data?.message);
                resetData();
            }
        } catch (error) {
            console.error('❌ Erreur lors de l\'appel allcountprep:', error);
            resetData();
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour réinitialiser les données en cas d'erreur
    const resetData = () => {
        setData({
            shipments: {
                total_count: 0,
                by_status: { draft: 0, validated: 0, closed: 0, cancelled: 0 }
            },
            supplier_orders: {
                total_count: 0,
                by_status: { draft: 0, validated: 0, closed: 0, cancelled: 0 }
            },
            preparation_orders: {  // Mise à jour de la réinitialisation
                total_count: 0,
                comptoir: {
                    total_count: 0,
                    by_status: { draft: 0, validated: 0, closed: 0, cancelled: 0 }
                },
                franchises: {
                    total_count: 0,
                    by_status: { draft: 0, validated: 0, closed: 0, cancelled: 0 }
                }
            },
            pending_orders: { comptoir: 0, franchises: 0, total: 0 },
            summary: { total_documents: 0 }
        });
    };

    // ============================================
    // Fonctions de navigation
    // ============================================
    const handleNavigateToShipments = () => {
        navigate('/expeditions');
    };

    const handleNavigateToSupplierOrders = () => {
        navigate('/stock');
    };

    const handleNavigateToPreparations = () => {  // Changé de handleNavigateToReceptions
        navigate('/preparation'); // À ajuster selon votre route
    };

    const handleNavigateToPendingOrders = () => {
        navigate('/CommandesEnAttentes');
    };

    // Charger les données au montage du composant et quand les filtres changent
    useEffect(() => {
        fetchAllData();
    }, [user?.entity, JSON.stringify(dateFilter)]);

    // ============================================
    // Widgets avec les données allcountprep
    // ============================================
    const ecomWidgets = [
        {
            id: 1,
            label: "Expéditions",
            badgeClass: data.shipments.total_count > 0 ? "success" : "danger",
            badge: data.shipments.total_count > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: data.shipments.total_count > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.shipments.total_count,
            decimals: 0,
            link: "Voir expéditions",
            onClick: handleNavigateToShipments,
            bgcolor: "primary",
            icon: "ri-truck-line",
            loading: loading,
            tooltipId: "shipments-tooltip",
            subItems: [
                { label: "Brouillons", value: data.shipments.by_status.draft },
                { label: "Validées", value: data.shipments.by_status.validated },
                { label: "Clôturées", value: data.shipments.by_status.closed },
                { label: "Annulées", value: data.shipments.by_status.cancelled }
            ]
        },
        {
            id: 2,
            label: "Commandes fournisseurs",
            badgeClass: data.supplier_orders.total_count > 0 ? "success" : "danger",
            badge: data.supplier_orders.total_count > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: data.supplier_orders.total_count > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.supplier_orders.total_count,
            decimals: 0,
            link: "Voir commandes fournisseurs",
            onClick: handleNavigateToSupplierOrders,
            bgcolor: "info",
            icon: "ri-store-2-line",
            loading: loading,
            tooltipId: "supplier-orders-tooltip",
            subItems: [
                { label: "Brouillons", value: data.supplier_orders.by_status.draft },
                { label: "Validées", value: data.supplier_orders.by_status.validated },
                { label: "Clôturées", value: data.supplier_orders.by_status.closed },
                { label: "Annulées", value: data.supplier_orders.by_status.cancelled }
            ]
        },
        {
            id: 3,
            label: "Commandes en préparation",  // Changé de "Réceptions"
            badgeClass: data.preparation_orders.total_count > 0 ? "success" : "danger",
            badge: data.preparation_orders.total_count > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: data.preparation_orders.total_count > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.preparation_orders.total_count,
            decimals: 0,
            link: "Voir préparations",
            onClick: handleNavigateToPreparations,  // Changé
            bgcolor: "warning",
            icon: "ri-file-copy-line",  // Changé l'icône
            loading: loading,
            tooltipId: "preparations-tooltip",  // Changé
            subItems: [
                { label: "Comptoir", value: data.preparation_orders.comptoir.total_count },
                { label: "Franchises", value: data.preparation_orders.franchises.total_count },
                { 
                    label: "Détails comptoir", 
                    value: `V:${data.preparation_orders.comptoir.by_status.validated} C:${data.preparation_orders.comptoir.by_status.closed}`,
                    isDetail: true 
                },
                { 
                    label: "Détails franchises", 
                    value: `V:${data.preparation_orders.franchises.by_status.validated} C:${data.preparation_orders.franchises.by_status.closed}`,
                    isDetail: true 
                }
            ]
        },
        {
            id: 4,
            label: "Commandes en attente",
            badgeClass: data.pending_orders.total > 0 ? "success" : "danger",
            badge: data.pending_orders.total > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: data.pending_orders.total > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.pending_orders.total,
            decimals: 0,
            link: "Voir commandes en attente",
            onClick: handleNavigateToPendingOrders,
            bgcolor: "danger",
            icon: "ri-hourglass-line",
            loading: loading,
            tooltipId: "pending-orders-tooltip",
            subItems: [
                { label: "Comptoir", value: data.pending_orders.comptoir },
                { label: "Franchises", value: data.pending_orders.franchises }
            ]
        }
    ];

    return (
        <React.Fragment>
            {ecomWidgets.map((item) => (
                <Col xl={3} md={6} key={item.id}>
                    <Card className="card-animate">
                        <CardBody>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1 overflow-hidden">
                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                        {item.label}
                                        {item.loading && <span className="ms-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                                    </p>
                                </div>
                                <div className="flex-shrink-0">
                                    <h5 className={"fs-14 mb-0 text-" + item.badgeClass}>
                                        {item.badge ? <i className={"fs-13 align-middle " + item.badge}></i> : null} {item.percentage} %
                                    </h5>
                                </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-4">
                                <div>
                                    <h4 className="fs-20 fw-semibold ff-secondary mb-4">
                                        <span className="counter-value" id={item.tooltipId}>
                                            {item.loading ? (
                                                <span className="text-muted">...</span>
                                            ) : (
                                                <CountUp
                                                    start={0}
                                                    prefix={item.prefix}
                                                    suffix={item.suffix}
                                                    separator={item.separator}
                                                    end={item.counter}
                                                    decimals={item.decimals}
                                                    duration={4}
                                                />
                                            )}
                                        </span>
                                    </h4>
                                    
                                    <button 
                                        onClick={item.onClick} 
                                        className="text-decoration-underline btn btn-link p-0"
                                        style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer' }}
                                    >
                                        {item.link}
                                    </button>

                                    {/* Tooltip avec détails par statut */}
                                    {!item.loading && item.subItems && (
                                        <Tooltip
                                            placement="bottom"
                                            isOpen={tooltipOpen[item.tooltipId]}
                                            target={item.tooltipId}
                                            toggle={() => toggleTooltip(item.tooltipId)}
                                        >
                                            <div className="text-start">
                                                {item.subItems.map((subItem, index) => (
                                                    <div key={index} className="d-flex justify-content-between gap-3">
                                                        <span>{subItem.label}:</span>
                                                        <span className={`fw-bold ms-2 ${subItem.isDetail ? 'text-muted small' : ''}`}>
                                                            {subItem.value}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                                <div className="avatar-sm flex-shrink-0">
                                    <span className={"avatar-title rounded fs-3 bg-" + item.bgcolor + "-subtle"}>
                                        <i className={`text-${item.bgcolor} ${item.icon}`}></i>
                                    </span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            ))}

            
        </React.Fragment>
    );
};

export default WidgetsE;